import { useQueryClient } from '@tanstack/react-query';
import useFriends from '@/hooks/contacts/use-friends';
import useSendMessage, {
  generateClientMessageId,
  generateMessagePackage,
} from '@/hooks/conversations/use-send-message';
import { allChatSessionQueryOptions } from '@/hooks/sessions/use-conversation-sessions';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { clearLastPosition, useConversationSessionStore } from '@/stores/conversation-session';
import { generateSessionKey } from '@/utils/session';

export default function useSendContact(type: ConversationType, id: Nullable<number>) {
  const queryClient = useQueryClient();
  const { friends } = useFriends();
  const { mutateAsync: sendMessage, isPending: isLoading } = useSendMessage();

  const sendContact = async (friendId: Nullable<number>): Promise<boolean> => {
    if (!friendId || !id || isLoading) return false;

    const friend = friends[friendId];
    if (!friend) return false;

    // 生成会话键并获取表单数据
    const sessionKey = generateSessionKey(type, id);
    const draft = useConversationSessionStore.getState().drafts[sessionKey];

    const message = generateMessagePackage(type, id, {
      Content: JSON.stringify({
        HeadImg: friend.FriendHeadImg,
        UserID: friend.FriendID,
        NickName: friend.FriendNickName,
        VChatID: friend.VChatID,
      }),
      Quote: draft?.Quote,
      ContentType: ConversationMessageContentType.Contact,
    });
    message.ClientMsgId = generateClientMessageId(message);

    await sendMessage(message);

    await queryClient.refetchQueries(allChatSessionQueryOptions);

    // 删除草稿数据，触发表单重置
    useConversationSessionStore.getState().deleteDraft(sessionKey);

    clearLastPosition(
      type === 'group'
        ? ROUTES.MESSAGES.GROUPS.$buildPath({ params: { id } })
        : ROUTES.MESSAGES.SESSIONS.$buildPath({ params: { id } }),
    );

    return true;
  };

  return {
    sendContact,
    isLoading,
  };
}
